'use client';

import type {
  ActionKitAction,
  ActionKitErrorBase,
  InferActionKitTypes,
} from '../action-kit';
import {
  ActionKitImpErrorDefault,
  ActionKitImpErrorUnauthenticated,
} from '../action-kit-imp';
import type { MaxTwoArgs } from '../fetch-context';
import type { LoaderImpOptions } from './types';
import { createLoader } from '../loader/createLoader';
import type { OnLoaderErrorHandlers } from '../loader/types';
import { DisplayInfo } from '@octano/global-ui';

export function createLoaderImp<
  Fn extends ActionKitAction<
    (...args: MaxTwoArgs<any[]>) => Promise<any>,
    ActionKitErrorBase
  >
>({ action, onError }: LoaderImpOptions<Fn>) {
  type Infer = InferActionKitTypes<Fn>;
  type Result = Infer['result'];
  type Args = Infer['args'];
  type TError = Infer['error'];

  const {
    useLoader,
    useLoaderData,
    LoaderConsumer,
    LoaderConsumerSimple,
    LoaderProvider,
  } = createLoader<Result, Args, TError, ActionKitImpErrorDefault>({
    action,
    options: {
      onActionError: (error) => {
        if (
          error.code === 'unknown' &&
          error.data instanceof Error &&
          error.data.message === 'Unauthenticated'
        ) {
          return {
            code: 'unauthenticated' as const,
            data: error.data,
          } as ActionKitImpErrorUnauthenticated;
        }
        return undefined;
      },
      onError: ({
        ...(onError as
          | OnLoaderErrorHandlers<Exclude<TError, ActionKitImpErrorDefault>>
          | undefined),
        ...({
          unauthenticated: (error) => {
            if (
              onError &&
              'unauthenticated' in onError &&
              onError['unauthenticated']
            ) {
              return onError['unauthenticated'](error);
            }
            return (
              <DisplayInfo
                title="Sesión expirada"
                textBody="Tu sesión ha expirado. Presiona continuar para ir al inicio de sesión"
              />
            );
          },
        } as OnLoaderErrorHandlers<ActionKitImpErrorDefault>),
      } as unknown) as OnLoaderErrorHandlers<TError>,
    },
  });

  return {
    useLoader,
    useLoaderData,
    LoaderConsumer,
    LoaderConsumerSimple,
    LoaderProvider,
  };
}
